import {action, computed, observable, runInAction, toJS} from 'mobx';
import isUndefined from 'lodash/isUndefined';
import isEmpty from 'lodash/isEmpty';
import request from '../../../utils/request';
import {FEED, MY_COLLECTION, TRACK} from '../../../constants/urlConstants';
import {TrackPlayStatus, TrackType} from '../../../constants/enums';
import {uploadToS3} from '../../../utils/awsUtility';
import {getOwnerDetails, isAdmin, isGuest, isOwner} from '../../../utils/userUtility';
import {analyzeBuffer, convertToMp3, isMidi, readFileAsArray} from '../../../utils/audioUtility';
import Player from '../../../stores/PlayerStore';
import looperStore from '../../create/looper/store';
import mainStore from '../store';
import uploadsProgressStore from '../../common/uploadsProgress/store'
import {guid} from '../../../utils/commonUtility';
import {RESTRICTED_BUCKET} from "../../../constants/constants";


class TrackStore extends Player {
    @observable isFetching = false;
    @observable trackArray = [];
    @observable ownedTracks = [];
    @observable pageCount = 1;
    @observable ownedTracksPageCount = 0;
    @observable ownedTracksCurrentPage = 0;
    @observable currentPage = 0;
    @observable isTrackDragging = false;

    /**
     * get all available feed tracks
     * @returns {Array}
     */
    @computed
    get feed() {
        return this.trackArray.slice();
    }

    /**
     * Get all owned tracks
     * @returns {Array}
     */
    @computed
    get uploadedTracks() {
        return this.ownedTracks.slice()
            .filter(track => isOwner(track.owner.id) && track.type !== TrackType.Project)
            .sort((a, b) => Date.parse(b.created) - Date.parse(a.created));
    }

    @action addTrackToUpload = (track) => {
        this.ownedTracks.unshift(track);
    };

    /**
     * Get all owned tracks
     * @returns {Array}
     */
    @computed
    get sharedProjectTracks() {
        return this.ownedTracks.slice()
            .filter(track => isOwner(track.owner.id) && track.type === TrackType.Project);
    }

    /**
     * get all available feed tracks
     * @returns {Array}
     */
    @computed
    get tracks() {
        return this.trackArray.slice();
    }

    /**
     * stops all playing tracks, except track given in params (if null, all tracks are stopped)
     *
     * @param track
     */
    stopAllTracks(track = null) {
        const id = track === null ? null : track.id;
        this.trackArray.concat(this.ownedTracks)
            .filter(curTrack => curTrack.playStatus === TrackPlayStatus.Playing && curTrack.id !== id)
            .forEach(curTrack => this.togglePlayPause(curTrack, TrackPlayStatus.Stopped));
    }

    @action updateUploadProgress = (fileId, progress) => {
        const selectedTrack = this.trackArray.find(t => t.fileId === fileId);
        if (selectedTrack) {
            selectedTrack.loadingProgress = (progress.loaded / progress.total) * 40;
        }
    };
    /**
     * Mark track as in favorite list or not;
     * @param trackID   {String}    The track id
     * @param setIsInFavorites   {Boolean}    Force state regardless of the current state
     */
    @action toggleIsFavorite = (trackID, setIsInFavorites) => {
        const selectedTrack = this.trackArray.find(t => t.id === trackID);
        selectedTrack.inFavorites = (!isUndefined(setIsInFavorites)) ? setIsInFavorites : !selectedTrack.inFavorites;
    };

    @action setCurrentPage = (val) => {
        this.currentPage = val;
    };

    @action setOwnedTracksCurrentPage = (val) => {
        this.ownedTracksCurrentPage = val;
    };

    /**
     * Update Track field
     * @param trackID {String}    The track id to update
     * @param field {String}    The track field to update
     * @param value    {*}   The new value to set into the track field
     */
    @action updateField = (trackID, field, value) => {
        const selectedTrack = this.trackArray.concat(this.ownedTracks).find(t => t.id === trackID);
        if (!isEmpty(selectedTrack)) {
            selectedTrack[field] = (!isUndefined(value)) ? value : selectedTrack[field] + 1;
        }
    };

    /**
     * Reset track to the Pristine state
     * @param track {Object}    The track to make pristine
     */
    @action resetTrack = (track) => {
        track.playStatus = TrackPlayStatus.Stopped;
        track.endTime = null;
        this.togglePlayPause(track, TrackPlayStatus.Stopped);
    };

    /**
     * Fetch s3 data and append to track
     * @param track {Object}    The track apply to
     * @returns {Promise.<void>}
     */
    @action
    async loadTrackAudioData(track) {
        const selectedTrack = this.trackArray.find(t => t.id === track.id) || this.ownedTracks.find(t => t.id === track.id);
        if (!isEmpty(selectedTrack)) {
            if (!selectedTrack.playerID && !selectedTrack.loaded && !selectedTrack.subscribing) {
                await this.subscribe(selectedTrack, false, true, true);
            }
        }
    }


    /**
     * Fetch all tracks
     * @returns {Promise.<void>}
     */
    @action fetchTracks = async (str, bpmRange) => {
        const query = str;
        const page = this.currentPage;
        if (this.pageCount === 0 || this.pageCount > this.currentPage) {
            this.isFetching = true;
            try {
                const options = {q: query, minBpm: bpmRange.min, maxBpm: bpmRange.max};
                const resp = await request(`${isGuest() ? 'public/' : ''}${FEED}`, {page, pageSize: 30, ...options});
                const {tracks} = resp;
                runInAction(() => {
                    this.pageCount = resp.pages;
                    const serializedTracks = this.prepareTracks(tracks);
                    this.trackArray = !page ? serializedTracks : this.trackArray.concat(serializedTracks);
                    this.isFetching = false;
                    this.currentPage = +resp.page + 1;
                });
            } catch (err) {
                console.log(err);
                runInAction(() => {
                    this.isFetching = false;
                });
            }
        }
    };

    /**
     * Fetch all owner tracks
     * @returns {Promise.<void>}
     */
    @action fetchOwnedTracks = async (str, bpmRange) => {
        const query = str;
        const page = this.ownedTracksCurrentPage;
        if (this.ownedTracksPageCount === 0 || this.ownedTracksPageCount > this.ownedTracksCurrentPage) {
            this.isFetching = true;
            try {
                const options = {q: query, minBpm: bpmRange.min, maxBpm: bpmRange.max};
                const resp = await request(MY_COLLECTION, {page, pageSize: 20, ...options});
                const {tracks} = resp;
                runInAction(() => {
                    this.ownedTracksPageCount = resp.pages;
                    const serializedTracks = this.prepareTracks(tracks);
                    this.ownedTracks = !page ? serializedTracks : this.ownedTracks.concat(serializedTracks);
                    this.isFetching = false;
                    this.ownedTracksCurrentPage = +resp.page + 1;
                });
            } catch (err) {
                runInAction(() => {
                    this.isFetching = false;
                });
            }
        }
    };


    /**
     * Delete a track
     * @param track {Object}    The track to delete
     * @param type  {String}    The track type
     * @returns {Promise.<void>}
     */
    @action
    async deleteTrack(track, type) {
        try {
            this.togglePlayPause(track, TrackPlayStatus.Stopped);
            request(`${TRACK}/${track.id}`, {method: 'DELETE', body: {source: track.type || type}});
            runInAction(() => {
                const selectedTrackIndex = this.ownedTracks.findIndex(t => t.id === track.id);
                this.ownedTracks.splice(selectedTrackIndex, 1);
                looperStore.removeFromLooper(track);
                const trackIndex = this.ownedTracks.findIndex(t => t.id === track.id);
                if (trackIndex !== -1) {
                    this.tracks.splice(trackIndex, 1);
                }
            });
        } catch (err) {
            throw err;
        }
    }

    /**
     * Save a track create/patch
     * @param track {Object}    The track data to save
     * @param trackBy   {String}    Optional parameter to look for track existing or not
     * @param type  {String}    Indicatior whether or not to force save type
     * @param addToFeed
     * @returns {Promise.<void>}
     */
    @action
    async saveTrack(track, trackBy = 'id', type, addToFeed = false) {
        const selectedTrack = this.trackArray.find(t => t[trackBy] === track[trackBy])
            || this.ownedTracks.find(t => t[trackBy] === track[trackBy]);
        const reqTypes = type || (!isEmpty(selectedTrack)) ? 'POST' : 'POST';
        try {
            const resp = await request(TRACK, {method: reqTypes, body: track});
            const respTrack = this.initTrack(resp);
            runInAction(() => {
                if (selectedTrack) {
                    Object.assign(selectedTrack, toJS(respTrack));
                } else if (addToFeed) {
                    this.trackArray.unshift(respTrack);
                }
            });
            return respTrack;
        } catch (err) {
            console.error(`failed to save track: ${err}`);
        }
        return null;
    }

    @action duplicateTrack = async (track) => {
        mainStore.showProgressLoader();
        try {
            const newTrack = await this.saveTrack({
                ...track,
                addCount: undefined,
                bottomlineStart: undefined,
                bottomlineWidth: undefined,
                id: undefined,
                computedBPM: undefined,
                created: undefined,
                hideFromExplore: undefined,
                inFavorites: undefined,
                userId: undefined,
                user: undefined,
                updated: undefined,
                trackId: undefined,
                timestamp: undefined,
                toplineStart: undefined,
                toplineWidth: undefined,
                owner: undefined,
            });

            this.addTrackToUpload(newTrack);
        } finally {
            mainStore.hideProgressLoader();
        }
    };

    @action updateTrackBackgroundImage = async (trackId, file) => {
        mainStore.showProgressLoader();
        try {
            const key = `media/stem/${trackId}}`;
            await uploadToS3(file, key, file.type, null, 'musico-public-bucket');

            await runInAction(async () => {
                let track = this.ownedTracks.find(t => t.id === trackId);
                track.backgroundImage = `/${key}`;
                if (track) {
                    await this.saveTrack(track);
                }
            });
        } finally {
            mainStore.hideProgressLoader();
        }
    };

    storeAttachments = async (track) => {
        return Promise.all(track.attachments.map(attachment => {
            if (attachment.file.size) {
                return uploadToS3(attachment.file, `attachments/${attachment.id}`, attachment.type, this.updateUploadProgress, RESTRICTED_BUCKET, true);
            } else {
                return null;
            }
        }));
    };

    @action
    async updateData(fileId, data) {
        const object = this.trackArray.concat(this.ownedTracks).find(t => t.fileId === fileId);
        if (object && object.bpm !== data.bpm) {
            Object.assign(object, {bpm: data.bpm, originalBPM: data.bpm, originalDuration: data.duration});
            await looperStore.updateLooperTrackBpm(data.id, data.bpm);
        }
    }

    @action
    async addRnmf(fileId, data) {
        const object = this.trackArray.concat(this.ownedTracks).find(t => t.fileId === fileId);
        if (object) {
            let newTrack = Object.assign({}, object, {id: object.id + '_rnmf', name: 'split ' + object.name, fileId: fileId + '_rnmf'});
            await looperStore.addToLooper(newTrack);
        }
    }

    /**
     * Upload track
     * @param track {Object}    The track data to save
     * @param file  {Object}    The track file
     * @param duration
     * @param keepOriginalOwnerAndId
     * @param addToMyUploads
     * @param addToFeed
     * @returns {Promise.<void>}
     */
    @action
    async uploadTrack(track, file, duration = null, keepOriginalOwnerAndId = false, addToMyUploads = true, addToFeed = false) {
        let loadingTrack = Object.assign({}, this.initTrack(track), {isLoading: true, loadingProgress: 0});
        loadingTrack.bpm = track.userBPM || -1;
        if (!keepOriginalOwnerAndId) {
            loadingTrack = Object.assign({}, loadingTrack, getOwnerDetails());
            loadingTrack.id = guid();
        }
        if (addToFeed) {
            this.trackArray.unshift(loadingTrack);
        }
        try {
            uploadsProgressStore.addUpload(track.fileId, file.name || track.name);
            if (file && !track.previewError) {
                const arrayBuffer = await readFileAsArray(file);
                const metaData = await analyzeBuffer(arrayBuffer, file.type);
                let fileData;
                if (isMidi(file.type)) {
                    fileData = file;
                } else if (isAdmin() || file.type !== 'audio/mp3') {
                    fileData = await convertToMp3(metaData.buffer, track.forceMono);
                } else {
                    fileData = file;
                }
                track.duration = duration || metaData.duration;
                track.peaks = metaData.peaks;
                track.bpm = track.userBPM || -1;
                if (isMidi(file.type) && (track.userBPM < 0 || isNaN(track.userBPM))) {
                    track.userBPM = metaData.song.bpm[0] || 120;
                    track.bpm = track.userBPM;
                    track.computedBPM = track.userBPM;
                }
                const fileType = isMidi(file.type) ? 'audio/midi' : 'audio/mp3';
                track.fileType = fileType;
                await uploadToS3(fileData, `audio/${track.fileId}`, fileType, uploadsProgressStore.onUploadProgress, RESTRICTED_BUCKET, true);
            } else if (file && track.previewError) {
                track.bpm = track.userBPM || -1;
                track.fileType = file.type;
                await uploadToS3(file, `audio/${track.fileId}`, track.fileType, uploadsProgressStore.onUploadProgress, RESTRICTED_BUCKET, true);
            }

            if (track.attachments && track.attachments.length > 0) {
                await this.storeAttachments(track);
            }
            if (addToMyUploads) {
                const savedTrack = await this.saveTrack(track, 'fileId', 'POST', addToFeed);
                savedTrack.bpm = track.bpm || -1;
                setTimeout(async () => {
                    const currentTrack = this.trackArray.concat(this.ownedTracks).find(t => t.id === savedTrack.id);
                    if (!currentTrack || currentTrack.bpm === -1) {
                        const resp = await request(`${TRACK}/${savedTrack.id}`);
                        if (resp.bpm) {
                            await this.updateData(resp.fileId, resp);
                        }
                    }
                }, 30000);
                uploadsProgressStore.finishUpload(track.fileId);
                return savedTrack;
            }
            uploadsProgressStore.finishUpload(track.fileId);
            return track;
        }

        catch (err) {
            const selectedTrackIndex = this.trackArray.findIndex(t => t.fileId === track.fileId);
            if (selectedTrackIndex !== -1) {
                runInAction(() => {
                    this.trackArray.splice(selectedTrackIndex, 1);
                });
            }
            uploadsProgressStore.finishUpload(track.fileId, false, err);
            throw err;
        }
    }

    async saveNewTracks(tracks) {
        for (let i = 0; i < tracks.length; i += 1) {
            const trackToSave = tracks[i];
            try {
                const file = await convertToMp3(this.getBuffer(trackToSave), false);
                const tempTrack = Object.assign({}, trackToSave, {
                    name: `${trackToSave.name} (v${trackToSave.version})`,
                    type: TrackType.Track
                }); // clone track
                await
                    this.uploadTrack(tempTrack, file, null, true, false);
            } catch (err) {
                throw err;
            }
        }
    }

    @action setTrackDragging = (val) => {
        this.isTrackDragging = val;
    }
}

const trackStore = new TrackStore();
export default trackStore;
